import { cn } from '../../../lib/utils'

export interface ThreadStatusDotProps {
  hasActiveWorkflow?: boolean
  hasActiveAgent?: boolean
  pendingApprovalCount?: number
  hasScheduledMessage?: boolean
  size?: 'sm' | 'md'
  /** Border only, no fill — used for selected contact group header */
  outline?: boolean
  className?: string
}

export interface ThreadStatusDotRailProps extends ThreadStatusDotProps {
  /** Dotted connector from row top to dot center */
  connectorAbove?: boolean
  /** Dotted connector from dot center to row bottom */
  connectorBelow?: boolean
  /** Extend connector into adjacent row padding (matches py-1.5 = 0.375rem) */
  connectorExtendAbove?: string
  connectorExtendBelow?: string
  connectorClassName?: string
}

/** Border color for outline dot and parent→child connector line. */
export function getThreadStatusDotLineClass(
  props: Pick<
    ThreadStatusDotProps,
    | 'hasActiveWorkflow'
    | 'hasActiveAgent'
    | 'pendingApprovalCount'
    | 'hasScheduledMessage'
  >,
): string {
  const isActive = props.hasActiveWorkflow || props.hasActiveAgent
  const hasApproval = (props.pendingApprovalCount ?? 0) > 0

  if (isActive) return 'border-foreground'
  if (hasApproval) return 'border-orange-500'
  if (props.hasScheduledMessage) return 'border-blue-500'
  return 'border-muted-foreground/40'
}

/**
 * Status indicator dot for thread tiles.
 *
 * Priority (highest to lowest):
 * 1. Active Workflow/Agent - Pulsing foreground (actively doing work)
 * 2. Pending Approval - Static orange (waiting for human action)
 * 3. Scheduled Message - Static blue (scheduled for later)
 * 4. Default - Static grey (idle)
 */
export function ThreadStatusDot({
  hasActiveWorkflow,
  hasActiveAgent,
  pendingApprovalCount,
  hasScheduledMessage,
  size = 'sm',
  outline = false,
  className,
}: ThreadStatusDotProps) {
  const isActive = hasActiveWorkflow || hasActiveAgent
  const hasApproval = (pendingApprovalCount ?? 0) > 0

  const sizeClass = size === 'sm' ? 'h-2 w-2' : 'h-2.5 w-2.5'

  if (outline) {
    if (isActive) {
      return (
        <span
          className={cn('relative flex shrink-0', sizeClass, className)}
          title="Active workflow or agent"
        >
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full border border-foreground opacity-75" />
          <span
            className={cn(
              'relative inline-flex rounded-full border border-foreground bg-transparent',
              sizeClass,
            )}
          />
        </span>
      )
    }

    if (hasApproval) {
      return (
        <span
          className={cn(
            'rounded-full border border-orange-500 bg-transparent shrink-0',
            sizeClass,
            className,
          )}
          title={`${pendingApprovalCount} pending approval${pendingApprovalCount !== 1 ? 's' : ''}`}
        />
      )
    }

    if (hasScheduledMessage) {
      return (
        <span
          className={cn(
            'rounded-full border border-blue-500 bg-transparent shrink-0',
            sizeClass,
            className,
          )}
          title="Scheduled message pending"
        />
      )
    }

    return (
      <span
        className={cn(
          'rounded-full border border-muted-foreground/40 bg-transparent shrink-0',
          sizeClass,
          className,
        )}
      />
    )
  }

  if (isActive) {
    return (
      <span
        className={cn('relative flex shrink-0', sizeClass, className)}
        title="Active workflow or agent"
      >
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-foreground opacity-75" />
        <span
          className={cn(
            'relative inline-flex rounded-full bg-foreground',
            sizeClass,
          )}
        />
      </span>
    )
  }

  if (hasApproval) {
    return (
      <span
        className={cn(
          'rounded-full bg-orange-500 shrink-0',
          sizeClass,
          className,
        )}
        title={`${pendingApprovalCount} pending approval${pendingApprovalCount !== 1 ? 's' : ''}`}
      />
    )
  }

  if (hasScheduledMessage) {
    return (
      <span
        className={cn(
          'rounded-full bg-blue-500 shrink-0',
          sizeClass,
          className,
        )}
        title="Scheduled message pending"
      />
    )
  }

  return (
    <span
      className={cn(
        'rounded-full bg-muted-foreground/40 shrink-0',
        sizeClass,
        className,
      )}
    />
  )
}

/** Left rail wrapper — vertically centers dot across multi-line tiles. */
export function ThreadStatusDotRail({
  connectorAbove,
  connectorBelow,
  connectorExtendAbove,
  connectorExtendBelow,
  connectorClassName,
  className,
  size = 'sm',
  ...props
}: ThreadStatusDotRailProps) {
  const dotHalf = size === 'md' ? '0.3125rem' : '0.25rem'

  return (
    <div className="relative flex w-2 shrink-0 self-stretch items-center justify-center">
      {connectorAbove && (
        <div
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute left-1/2 w-px -translate-x-1/2 border-l border-dotted',
            connectorClassName,
          )}
          style={{
            top: connectorExtendAbove
              ? `calc(-1 * ${connectorExtendAbove})`
              : 0,
            bottom: `calc(50% + ${dotHalf})`,
          }}
        />
      )}
      {connectorBelow && (
        <div
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute left-1/2 w-px -translate-x-1/2 border-l border-dotted',
            connectorClassName,
          )}
          style={{
            top: `calc(50% + ${dotHalf})`,
            bottom: connectorExtendBelow
              ? `calc(-1 * ${connectorExtendBelow})`
              : 0,
          }}
        />
      )}
      <ThreadStatusDot
        {...props}
        size={size}
        className={cn('relative z-[1]', className)}
      />
    </div>
  )
}
